import { useState, useEffect } from 'react';
import {
  Users, X, Plus, Trash2, Tag, Check, KeyRound, Power, Save, Smartphone
} from 'lucide-react';
import { dealersAPI } from '../api';
import './GroupManager.css';
import './DealerManager.css';

/**
 * Админская модалка: дилеры (реселлеры) — логин/пароль и группы, которые им видны.
 * Дилер видит только файлы подписок из отмеченных здесь групп и только ключи этих файлов.
 */
function DealerManager({ groups, onClose }) {
  const [dealers, setDealers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // форма создания
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [newGroups, setNewGroups] = useState([]);
  const [newAppType, setNewAppType] = useState('any');
  const [creating, setCreating] = useState(false);

  // редактирование существующего
  const [editingId, setEditingId] = useState(null);
  const [editGroups, setEditGroups] = useState([]);
  const [editName, setEditName] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editAppType, setEditAppType] = useState('any');
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await dealersAPI.getAll();
      setDealers(res.data.dealers || []);
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const toggle = (list, id) =>
    list.includes(id) ? list.filter(g => g !== id) : [...list, id];

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword) {
      setError('Логин и пароль обязательны');
      return;
    }
    setCreating(true);
    try {
      await dealersAPI.create({
        username: newUsername.trim(),
        password: newPassword,
        name: newName.trim(),
        groups: newGroups,
        appType: newAppType,
      });
      setNewUsername(''); setNewPassword(''); setNewName(''); setNewGroups([]); setNewAppType('any');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (d) => {
    setEditingId(d.id);
    setEditGroups(d.groups || []);
    setEditName(d.name || '');
    setEditPassword('');
    setEditAppType(d.appType || 'any');
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await dealersAPI.update(id, {
        name: editName,
        groups: editGroups,
        appType: editAppType,
        ...(editPassword ? { password: editPassword } : {}),
      });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (d) => {
    try {
      await dealersAPI.update(d.id, { active: !d.active });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async (d) => {
    if (!confirm(`Удалить дилера "${d.username}"?\nОн больше не сможет войти. Файлы и ключи не трогаются.`)) return;
    try {
      await dealersAPI.delete(d.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const groupEntries = Object.entries(groups || {});

  // Тип приложения дилера: 'any' — раздаёт обычные ссылки и crypto4;
  // 'happ' — обычная ссылка у него скрыта, клиентам уходит только crypto4.
  const appTypePicker = (value, onChange) => (
    <div className="dealer-apptype">
      <span className="dealer-apptype-label"><Smartphone size={11} /> Приложение клиента:</span>
      <button
        type="button"
        className={`btn btn-sm ${value === 'any' ? 'btn-primary' : 'btn-secondary'}`}
        onClick={() => onChange('any')}
        title="Дилер может копировать и обычную ссылку подписки, и crypto4"
      >
        Любое
      </button>
      <button
        type="button"
        className={`btn btn-sm ${value === 'happ' ? 'btn-primary' : 'btn-secondary'}`}
        onClick={() => onChange('happ')}
        title="Обычная ссылка скрыта — дилер отдаёт клиентам только crypto4-ссылку"
      >
        Только Happ
      </button>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content group-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Users size={15} /> Дилеры</h3>
          <button onClick={onClose} className="modal-close"><X size={14} /></button>
        </div>

        {error && <div className="alert alert-error" style={{ margin: '0 20px 0' }}>{error}</div>}

        <div className="modal-body">
          {isLoading ? (
            <div className="dealer-loading"><div className="spinner" /> Загрузка...</div>
          ) : dealers.length === 0 ? (
            <p className="group-manager-empty">Дилеров пока нет. Создайте первого!</p>
          ) : (
            <div className="group-manager-list">
              {dealers.map(d => (
                <div key={d.id} className="group-manager-item">
                  <div className="group-manager-row">
                    <span className="group-manager-name">
                      <Users size={13} className="group-manager-name-icon" />
                      <span className="group-manager-name-text">{d.username}</span>
                      {d.name && <span className="dealer-subtitle">{d.name}</span>}
                      {d.appType === 'happ' && (
                        <span className="dealer-apptype-badge" title="Дилеру доступна только crypto4-ссылка">
                          <Smartphone size={10} /> только Happ
                        </span>
                      )}
                      {!d.active && <span className="dealer-off">отключён</span>}
                    </span>
                    <div className="group-manager-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => editingId === d.id ? setEditingId(null) : startEdit(d)}
                        title="Группы и пароль"
                      >
                        <KeyRound size={12} />
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleToggleActive(d)}
                        title={d.active ? 'Отключить вход' : 'Включить вход'}
                      >
                        <Power size={12} color={d.active ? 'var(--success)' : 'var(--danger)'} />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(d)} title="Удалить">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>

                  <div className="dealer-groups">
                    {(d.groups || []).length === 0
                      ? <span className="dealer-hint">Групп нет — дилер не увидит ни одной подписки</span>
                      : (d.groups || []).map(gid => (
                        <span key={gid} className="dealer-group-chip">
                          <Tag size={10} /> {groups?.[gid]?.name || gid}
                        </span>
                      ))}
                  </div>

                  {editingId === d.id && (
                    <div className="dealer-edit-box">
                      <input
                        className="input"
                        placeholder="Имя (для себя)"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                      />
                      <input
                        className="input"
                        placeholder="Новый пароль (пусто — не менять)"
                        value={editPassword}
                        onChange={e => setEditPassword(e.target.value)}
                      />
                      {appTypePicker(editAppType, setEditAppType)}
                      <div className="dealer-groups">
                        {groupEntries.map(([id, g]) => (
                          <button
                            type="button"
                            key={id}
                            className={`btn btn-sm ${editGroups.includes(id) ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setEditGroups(prev => toggle(prev, id))}
                          >
                            {editGroups.includes(id) ? <Check size={11} /> : <Tag size={11} />} {g.name}
                          </button>
                        ))}
                      </div>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleSave(d.id)}
                        disabled={saving}
                      >
                        <Save size={12} /> {saving ? 'Сохранение...' : 'Сохранить'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleCreate} className="group-create-form">
            <span className="form-label">Новый дилер</span>
            <div className="dealer-create-row">
              <input
                className="input"
                placeholder="Логин"
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
              />
              <input
                className="input"
                placeholder="Пароль"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
              <input
                className="input"
                placeholder="Имя (для себя)"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
            </div>
            {appTypePicker(newAppType, setNewAppType)}
            <div className="dealer-groups">
              {groupEntries.length === 0
                ? <span className="dealer-hint">Сначала создайте группы — дилеру нечего будет назначить</span>
                : groupEntries.map(([id, g]) => (
                  <button
                    type="button"
                    key={id}
                    className={`btn btn-sm ${newGroups.includes(id) ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setNewGroups(prev => toggle(prev, id))}
                  >
                    {newGroups.includes(id) ? <Check size={11} /> : <Tag size={11} />} {g.name}
                  </button>
                ))}
            </div>
            <button type="submit" className="btn btn-success" disabled={creating || !newUsername.trim() || !newPassword}>
              <Plus size={13} /> {creating ? 'Создание...' : 'Добавить дилера'}
            </button>
          </form>

          <p className="dealer-hint dealer-hint--footer">
            Дилер входит теми же логином и паролем на этой же странице. Он видит только подписки
            отмеченных групп и их ключи; сами VPN-ссылки, VPS, биллинг и группы ему недоступны.
          </p>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Закрыть</button>
        </div>
      </div>
    </div>
  );
}

export default DealerManager;
